import { useState } from "react"
import { Input } from "@cloudflare/kumo/components/input"
import { Loader, SkeletonLine } from "@cloudflare/kumo/components/loader"
import { Text } from "@cloudflare/kumo/components/text"
import { CaretRightIcon } from "@phosphor-icons/react"
import { ScrollArea } from "@/components/kumo/scroll-area"
import {
  filterChooseWorkspaceMemberships,
  type ChooseWorkspaceMembership,
  type ChooseWorkspaceViewState,
} from "@/modules/authorization/login-choose-organization"
import {
  StandaloneNote,
  StandaloneTitle,
} from "@/components/kumo/standalone-card"

// Above this many workspaces the list gets a search field.
const SEARCH_THRESHOLD = 6

export function ChooseWorkspaceView({
  view,
  signedInAs,
  selectingOrganizationId,
  errorMessage,
  onSelect,
}: {
  view: ChooseWorkspaceViewState
  signedInAs?: string
  selectingOrganizationId: string | null
  errorMessage: string
  onSelect: (membership: ChooseWorkspaceMembership) => void
}) {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1.5">
        <StandaloneTitle>Choose workspace</StandaloneTitle>
        {signedInAs ? (
          <Text variant="secondary">
            Signed in as <span className="text-kumo-default">{signedInAs}</span>
          </Text>
        ) : null}
      </div>

      {errorMessage ? (
        <StandaloneNote tone="error">{errorMessage}</StandaloneNote>
      ) : null}

      {view.status === "loading" ? (
        <div className="divide-y divide-kumo-line overflow-hidden rounded-lg border border-kumo-line" aria-busy="true">
          <span className="sr-only" role="status">{view.message}</span>
          {[64, 48, 56].map((width) => (
            <div key={width} className="flex h-12 items-center gap-3 px-3">
              <span className="size-7 shrink-0 rounded-md bg-kumo-tint" />
              <span className="flex-1">
                <SkeletonLine minWidth={width} maxWidth={width} />
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {view.status === "error" || view.status === "empty" ? (
        <StandaloneNote tone="error">{view.message}</StandaloneNote>
      ) : null}

      {view.status === "ready" ? (
        <WorkspaceList
          memberships={view.memberships}
          selectingOrganizationId={selectingOrganizationId}
          onSelect={onSelect}
        />
      ) : null}
    </div>
  )
}

function WorkspaceList({
  memberships,
  selectingOrganizationId,
  onSelect,
}: {
  memberships: ChooseWorkspaceMembership[]
  selectingOrganizationId: string | null
  onSelect: (membership: ChooseWorkspaceMembership) => void
}) {
  const [query, setQuery] = useState("")
  const searchable = memberships.length > SEARCH_THRESHOLD
  const shown = filterChooseWorkspaceMemberships(memberships, query)
  const isSelecting = selectingOrganizationId != null

  return (
    <div className="grid gap-3">
      {searchable ? (
        <Input
          aria-label="Search workspaces"
          placeholder="Search workspaces"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      ) : null}
      {shown.length === 0 ? (
        <Text variant="secondary">No workspaces match “{query.trim()}”.</Text>
      ) : (
        <ScrollArea className="rounded-lg border border-kumo-line" viewportClassName="max-h-[296px]">
          <ul aria-label="Workspaces" className="divide-y divide-kumo-line">
            {shown.map((membership) => {
              const isThisSelecting = selectingOrganizationId === membership.organizationId
              return (
                <li key={membership.id}>
                  <button
                    type="button"
                    disabled={isSelecting}
                    aria-busy={isThisSelecting || undefined}
                    onClick={() => onSelect(membership)}
                    className="flex h-12 w-full cursor-pointer items-center gap-3 bg-kumo-base px-3 text-left hover:bg-kumo-tint focus-visible:bg-kumo-tint focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-kumo-ring disabled:cursor-default disabled:hover:bg-kumo-base"
                  >
                    <span
                      aria-hidden
                      className="grid size-7 shrink-0 place-items-center rounded-md bg-kumo-base font-medium text-kumo-strong border border-kumo-line"
                    >
                      {initial(membership.organizationName)}
                    </span>
                    <span className={`min-w-0 flex-1 truncate ${isSelecting && !isThisSelecting ? "text-kumo-subtle" : "text-kumo-default"}`}>
                      {isThisSelecting ? `Opening ${membership.organizationName}` : membership.organizationName}
                    </span>
                    {isThisSelecting ? (
                      <Loader size="sm" />
                    ) : (
                      <CaretRightIcon aria-hidden className="shrink-0 text-kumo-subtle" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </ScrollArea>
      )}
    </div>
  )
}

function initial(name: string) {
  return Array.from(name.trim())[0]?.toUpperCase() ?? "?"
}
